import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import {
    StreamingEvent,
    SpanNode,
    CognitiveStreamState,
    SpanInfo,
    isCognitiveEvent,
    isLLMToken,
    isRootSpan,
    CognitiveEventTypes,
    ProcessedEvent
} from './streaming-event-schema.interface';

/**
 * Manages the cognitive streaming state with proper span hierarchy
 * Based on the craig.ddd.v3.0 protocol from example-stream.txt
 */
@Injectable({
    providedIn: 'root'
})
export class CognitiveStateManagerService {
    // Observable state
    private stateSubject$ = new BehaviorSubject<CognitiveStreamState>(this.createInitialState());
    public state$: Observable<CognitiveStreamState> = this.stateSubject$.asObservable();

    // Current state snapshot
    private get state(): CognitiveStreamState {
        return this.stateSubject$.value;
    }

    constructor() {
        // console.log('🧠 CognitiveStateManager initialized');
    }

    /**
     * Creates a clean initial state
     */
    private createInitialState(): CognitiveStreamState {
        return {
            rootSpans: [],
            spanRegistry: new Map(),
            uiState: {
                isChainOfThoughtCollapsed: true // Default to COLLAPSED for cleaner UX
            },
            responseContent: '',
            responseTokens: [],
            totalEvents: 0,
            cognitiveEvents: 0,
            llmTokens: 0,
            nodesExecuted: 0,
            toolsExecuted: 0,
            isStreaming: false
        };
    }

    /**
     * Resets the state for a new streaming session
     */
    public resetState(): void {
        // console.log('🔄 Resetting cognitive state');
        this.stateSubject$.next(this.createInitialState());
    }

    /**
     * Starts a new streaming session
     */
    public startStreaming(): void {
        const state = this.state;
        state.isStreaming = true;
        state.streamStartTime = Date.now();
        // console.log('▶️ Started streaming session');
        this.emitState();
    }

    /**
     * Ends the streaming session
     */
    public endStreaming(): void {
        const state = this.state;
        state.isStreaming = false;
        state.streamEndTime = Date.now();
        if (state.streamStartTime) {
            state.totalDuration = state.streamEndTime - state.streamStartTime;
        }
        // console.log('⏹️ Ended streaming session, duration:', state.totalDuration);
        this.emitState();
    }

    /**
     * Main entry point for processing streaming events
     */
    public processEvent(event: StreamingEvent): void {
        const state = this.state;
        state.totalEvents++;
        state.lastEventTime = Date.now();

        if (isCognitiveEvent(event)) {
            this.processCognitiveEvent(event);
        } else if (isLLMToken(event)) {
            this.processLLMToken(event);
        }

        this.emitState();
    }

    /**
     * Process cognitive events (nodes, tools, etc.)
     */
    private processCognitiveEvent(event: StreamingEvent): void {
        const state = this.state;
        state.cognitiveEvents++;

        if (!event.custom_outputs) return;

        const { span, event_type, cognitive, node, tool, domain } = event.custom_outputs;

        if (!span) {
            console.warn('⚠️ Cognitive event without span info:', event);
            return;
        }

        // Get or create the span node
        let spanNode = state.spanRegistry.get(span.span_id);

        if (!spanNode) {
            spanNode = this.createSpanNode(span);
            state.spanRegistry.set(span.span_id, spanNode);

            // Add to hierarchy
            if (isRootSpan(span)) {
                state.rootSpans.push(spanNode);
                // console.log('🌳 Added root span:', span.span_name);
            } else if (span.parent_span_id) {
                const parentNode = state.spanRegistry.get(span.parent_span_id);
                if (parentNode) {
                    parentNode.children.push(spanNode);
                    parentNode.isExpanded = true; // Auto-expand parent
                    // console.log('🔗 Added child span:', span.span_name, 'to parent:', parentNode.span.span_name);
                } else {
                    console.warn('⚠️ Parent span not found:', span.parent_span_id);
                    // Add as root if parent not found
                    state.rootSpans.push(spanNode);
                }
            }
        }

        // Update span node with event data
        if (node) spanNode.node = node;
        if (tool) spanNode.tool = tool;
        if (cognitive) spanNode.cognitive = cognitive;
        if (domain) spanNode.domain = domain;

        // Add the event to the span's event list
        const processedEvent: ProcessedEvent = {
            chunk_id: event.chunk_id,
            event_type: event_type,
            delta: this.cleanDelta(event.delta || ''),
            timestamp: Date.now(),
            cognitive_phase: cognitive?.cognitive_phase,
            metadata: { cognitive, domain }
        };

        spanNode.events.push(processedEvent);

        // Handle lifecycle events
        this.handleLifecycleEvent(event_type, spanNode, cognitive);

        // Update metrics
        if (event_type === CognitiveEventTypes.NODE_COMPLETE) {
            state.nodesExecuted++;
        } else if (event_type === CognitiveEventTypes.TOOL_COMPLETE) {
            state.toolsExecuted++;
        }

        // Set active span
        if (event_type.includes('.start')) {
            state.activeSpanId = span.span_id;
            spanNode.isActive = true;
            spanNode.startTime = Date.now();
        } else if (event_type.includes('.complete')) {
            if (state.activeSpanId === span.span_id) {
                state.activeSpanId = undefined;
            }
            spanNode.isActive = false;
            spanNode.endTime = Date.now();
            if (spanNode.startTime) {
                spanNode.duration = spanNode.endTime - spanNode.startTime;
            }

            // Use the execution time from the event if available
            if (cognitive?.execution_time_ms) {
                spanNode.duration = cognitive.execution_time_ms;
            }
        }

        // console.log(`🧠 Processed ${event_type} for span ${span.span_name}`);
    }

    /**
     * Process LLM token events
     */
    private processLLMToken(event: StreamingEvent): void {
        const state = this.state;
        const token = event.delta || '';

        // Filter out cognitive markers from response
        if (this.isCognitiveMarker(token)) {
            // console.log('🚫 Filtered cognitive marker from response');
            return;
        }

        state.llmTokens++;
        state.responseTokens.push(token);
        state.responseContent += token;

        // console.log(`📝 Added token ${state.llmTokens}: "${token.substring(0, 20)}..."`);
    }

    /**
     * Creates a new span node
     */
    private createSpanNode(span: SpanInfo): SpanNode {
        return {
            span,
            events: [],
            children: [],
            isExpanded: span.depth === 0, // Expand root nodes by default
            isActive: false
        };
    }

    /**
     * Handle lifecycle events for spans
     */
    private handleLifecycleEvent(eventType: string, spanNode: SpanNode, cognitive?: any): void {
        // You can add specific handling for different lifecycle events here
        switch (eventType) {
            case CognitiveEventTypes.NODE_START:
                // console.log(`🚀 Node started: ${spanNode.span.span_name}`);
                break;
            case CognitiveEventTypes.NODE_COMPLETE:
                // console.log(`✅ Node completed: ${spanNode.span.span_name} in ${cognitive?.execution_time_ms}ms`);
                break;
            case CognitiveEventTypes.TOOL_START:
                // console.log(`🔧 Tool started: ${spanNode.span.span_name}`);
                break;
            case CognitiveEventTypes.TOOL_COMPLETE:
                // console.log(`✅ Tool completed: ${spanNode.span.span_name} in ${cognitive?.execution_time_ms}ms`);
                break;
        }
    }

    /**
     * Clean delta content (remove cognitive markers)
     */
    private cleanDelta(delta: string): string {
        return delta
            .replace(/\[.*?COGNITIVE.*?\]/g, '')
            .replace(/🧠|🔧|🔗|✅|🚀/g, '')
            .replace(/Starting cognitive processing:?/gi, '')
            .replace(/Completed cognitive processing:?/gi, '')
            .trim();
    }

    /**
     * Check if a token is a cognitive marker
     */
    private isCognitiveMarker(token: string): boolean {
        return (
            token.includes('COGNITIVE') ||
            token.includes('[ENTRY_') ||
            token.includes('[SIGNAL_') ||
            token.includes('[DETAIL_') ||
            token.includes('[COMPARE_') ||
            token.includes('Starting cognitive') ||
            token.includes('Completed cognitive') ||
            token.includes('🧠')
        );
    }

    /**
     * Emit the current state
     */
    private emitState(): void {
        this.stateSubject$.next({ ...this.state });
    }

    /**
     * Get a snapshot of the current state
     */
    public getStateSnapshot(): CognitiveStreamState {
        return {
            ...this.state,
            spanRegistry: new Map(this.state.spanRegistry),
            rootSpans: this.deepCloneSpans(this.state.rootSpans),
            // Preserve UI state when taking snapshot - DEFAULT TO OPEN/EXPANDED
            uiState: this.state.uiState ? { ...this.state.uiState } : { isChainOfThoughtCollapsed: false }
        };
    }

    /**
     * Deep clone spans for persistence
     */
    private deepCloneSpans(spans: SpanNode[]): SpanNode[] {
        return spans.map((span) => ({
            ...span,
            events: [...span.events],
            children: this.deepCloneSpans(span.children)
        }));
    }

    /**
     * Update UI state
     */
    public updateUIState(uiState: { isChainOfThoughtCollapsed: boolean }): void {
        const currentState = this.state;
        currentState.uiState = { ...currentState.uiState, ...uiState };
        this.emitState();
    }

    /**
     * Restore state from a snapshot
     */
    public restoreState(snapshot: CognitiveStreamState): void {
        // console.log('📥 Restoring cognitive state from snapshot');

        // Create a new state with the snapshot data
        const newState: CognitiveStreamState = {
            ...snapshot,
            spanRegistry: new Map(),
            isStreaming: false
        };

        // Rebuild the span registry
        const rebuildRegistry = (spans: SpanNode[]) => {
            spans.forEach((span) => {
                newState.spanRegistry.set(span.span.span_id, span);
                if (span.children && span.children.length > 0) {
                    rebuildRegistry(span.children);
                }
            });
        };

        rebuildRegistry(newState.rootSpans);

        this.stateSubject$.next(newState);
        // console.log('✅ State restored with', newState.rootSpans.length, 'root spans');
    }
}
